"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.timelessMetricTimeDimension = exports.isTimelessWithWarehousingEnabled = exports.isTimelessWithWarehousingDisabled = exports.getDateFormatter = exports.getFiscalYearStartMonth = exports.getMeasureFormatString = exports.getTimestampGranularity = exports.buildMeasureAccessor = exports.buildTimestampAccessor = exports.getMeasureFeature = exports.getTimestampFeature = exports.MEASURE_COLUMN_NAME = exports.TIMESTAMP_COLUMN_NAME = void 0;
var dateFormatter_1 = require("../formatting/dateFormatter");
var localization_1 = require("../localization");
var models_1 = require("../models");
var EffectiveDefinition_1 = require("../models/EffectiveDefinition");
var warehousing_1 = require("./warehousing");
exports.TIMESTAMP_COLUMN_NAME = 'timestamp';
exports.MEASURE_COLUMN_NAME = 'measure';
var getTimestampFeature = function (features) {
    return features.timestamp;
};
exports.getTimestampFeature = getTimestampFeature;
var getMeasureFeature = function (features) {
    return features.measure;
};
exports.getMeasureFeature = getMeasureFeature;
var buildTimestampAccessor = function (effectiveDefinition) {
    var timestamp = (0, exports.getTimestampFeature)(effectiveDefinition.features);
    return function (dataRow) { return dataRow[timestamp === null || timestamp === void 0 ? void 0 : timestamp.column]; };
};
exports.buildTimestampAccessor = buildTimestampAccessor;
var buildMeasureAccessor = function (effectiveDefinition) {
    var measure = (0, exports.getMeasureFeature)(effectiveDefinition.features);
    return function (dataRow) { return dataRow[measure.column]; };
};
exports.buildMeasureAccessor = buildMeasureAccessor;
var getTimestampGranularity = function (effectiveDefinition) {
    var _a, _b;
    var timestamp = (0, exports.getTimestampFeature)(effectiveDefinition.features);
    var column = (0, EffectiveDefinition_1.columnSchemaForRef)(effectiveDefinition, timestamp);
    // Phoenix needs to be backwards compatible with oldest supported version (2020.4 as of Feb '22),
    // which doesn't return a `type` for the ColumnSchema, so we are casting here rather than using
    // isDateTimeColumnSchema. Once the oldest supported version does use `type` (2021.1), this should
    // be modified to use isDateTimeColumnSchema instead.
    return (_b = (_a = column) === null || _a === void 0 ? void 0 : _a.dateGranularity) !== null && _b !== void 0 ? _b : models_1.DateGranularity.UNKNOWN;
};
exports.getTimestampGranularity = getTimestampGranularity;
var getMeasureFormatString = function (definition) {
    var _a;
    var measure = (0, exports.getMeasureFeature)(definition.features);
    var column = (0, EffectiveDefinition_1.columnSchemaForRef)(definition, measure);
    // Phoenix needs to be backwards compatible with oldest supported version (2020.4 as of Feb '22),
    // which doesn't return a `type` for the ColumnSchema, so we are casting here rather than using
    // isDateTimeColumnSchema. Once the oldest supported version does use `type` (2021.1), this should
    // be modified to use isDateTimeColumnSchema instead.
    return (_a = column) === null || _a === void 0 ? void 0 : _a.format;
};
exports.getMeasureFormatString = getMeasureFormatString;
var getFiscalYearStartMonth = function (definition) {
    var _a, _b;
    var timestamp = definition && (0, exports.getTimestampFeature)(definition.features);
    var column = definition && timestamp && (0, EffectiveDefinition_1.columnSchemaForRef)(definition, timestamp);
    // Phoenix needs to be backwards compatible with oldest supported version (2020.4 as of Feb '22),
    // which doesn't return a `type` for the ColumnSchema, so we are casting here rather than using
    // isDateTimeColumnSchema. Once the oldest supported version does use `type` (2021.1), this should
    // be modified to use isDateTimeColumnSchema instead.
    return (_b = (_a = column) === null || _a === void 0 ? void 0 : _a.fiscalYearStartMonth) !== null && _b !== void 0 ? _b : models_1.FiscalYearStartMonth.JANUARY;
};
exports.getFiscalYearStartMonth = getFiscalYearStartMonth;
var getDateFormatter = function (definition) {
    var granularity = definition ? (0, exports.getTimestampGranularity)(definition) : models_1.DateGranularity.UNKNOWN;
    var dateTrunc = (0, models_1.dateTruncFromGranularity)(granularity);
    var fiscalYearStartMonth = (0, exports.getFiscalYearStartMonth)(definition);
    return (0, dateFormatter_1.dateFormatterFactory)(dateTrunc, fiscalYearStartMonth);
};
exports.getDateFormatter = getDateFormatter;
/**
 * Primarily used to determine whether the metric should be displayed as a single number, what
 * information to include in the configuration section, and whether a new or edited config is valid.
 * Specifically, a metric where isTimelessWithWarehousingDisabled returns false cannot be saved if it
 * does not have a valid date window.
 *
 * @param definition the metric EffectiveDefinition
 * @returns true for timeless metrics that are not being warehoused, false otherwise
 */
var isTimelessWithWarehousingDisabled = function (definition) {
    var _a;
    // casting instead of using `isDateTimeColumnSchema` to support Phoenix accessing a 2020.4 server
    var timestampColumn = (_a = definition === null || definition === void 0 ? void 0 : definition.schema) === null || _a === void 0 ? void 0 : _a.timestamp;
    if (timestampColumn === undefined) {
        return true;
    }
    // With the release of snapshotting/warehousing, we no longer want to use, and are deprecating, isSnapshotTimestamp.
    // We also can't simply remove the old return since metrics-engine is used by Phoenix and may access older servers.
    if (timestampColumn.isTimeless !== undefined) {
        return timestampColumn.isTimeless && !(0, warehousing_1.isBeingWarehoused)(definition === null || definition === void 0 ? void 0 : definition.features);
    }
    return timestampColumn.isSnapshotTimestamp === true;
};
exports.isTimelessWithWarehousingDisabled = isTimelessWithWarehousingDisabled;
/**
 * Primarily used to determine whether the strings associated with the metric should read 'snapshotted' (if true)
 * or 'refreshed' (if false).
 *
 * @param definition the metric EffectiveDefinition
 * @returns true for timeless metrics that are being warehoused, false otherwise
 */
var isTimelessWithWarehousingEnabled = function (definition) {
    var _a;
    // casting instead of using `isDateTimeColumnSchema` to support Phoenix accessing a 2020.4 server
    var timestampColumn = (_a = definition === null || definition === void 0 ? void 0 : definition.schema) === null || _a === void 0 ? void 0 : _a.timestamp;
    if (timestampColumn === undefined) {
        return false;
    }
    if (timestampColumn.isTimeless !== undefined) {
        return timestampColumn.isTimeless && (0, warehousing_1.isBeingWarehoused)(definition === null || definition === void 0 ? void 0 : definition.features);
    }
    return false;
};
exports.isTimelessWithWarehousingEnabled = isTimelessWithWarehousingEnabled;
var timelessMetricTimeDimension = function (isWarehoused) {
    return isWarehoused ? localization_1.Messages.snapshotTimeDimension() : localization_1.Messages.noTimeDimensionPlaceholder();
};
exports.timelessMetricTimeDimension = timelessMetricTimeDimension;
//# sourceMappingURL=metricValue.js.map